
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ContexteUtilisateur, EtiquetteModel } from 'src/models/pilotage/contextePilotageUtilisateurModel';
import { ModifierUtilisateurPilotageModel } from 'src/models/pilotage/modifierUtilisateurPilotageModel';
import { RoleEntitePilotage } from 'src/models/pilotage/roleEntitePilotageModel';
import { UtilisateurPilotageModel } from 'src/models/pilotage/utilisateurPilotageModel';
import { EtiquetteService } from 'src/services/etiquette.service';
import { UtilisateurService } from 'src/services/utilisateur.service';

@Component({
    selector: 'app-utilisateur-edit',
    templateUrl: './utilisateur-edit.component.html',
    styleUrls: ['./utilisateur-edit.component.scss'],
    imports: [MatProgressSpinnerModule, TranslateModule, FormsModule]
})
export class UtilisateurEditComponent implements OnInit {

  user!: UtilisateurPilotageModel;
  utilisateurId: number | undefined;
  contexteUtilisateur!: ContexteUtilisateur;

  estPiloteGroupe: boolean = false;
  estRoot: boolean = false;
  estPilote: boolean = false;
  estAdmin: boolean = false;
  numeroEmploye: string = '';
  entitesAccessibles: { id: number, nom: string }[] = [];
  entitesDispoAdmin: { id: number, nom: string }[] = [];
  entiteAdmins: { id: number, nom: string }[] = [];
  entiteIdAdminSelectionnee: number | null = null;
  entiteIdVisioSelectionne: number | null = null;
  entitesVisionnement: { id: number, nom: string }[] = [];
  isLoading: boolean = true;
  estEnCoursDeEnregistrement: boolean = false;
  etiquettesDisponibles: EtiquetteModel[] = [];
  etiquettesSelectionnees: EtiquetteModel[] = [];
  etiquetteIdSelectionnee: number | null = null;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    public utilisateurService: UtilisateurService,
    public etiquetteService: EtiquetteService,
  ) { }

  ngOnInit(): void {
    this.utilisateurId = Number(this.route.snapshot.paramMap.get('id'));
    this.contexteUtilisateur = this.utilisateurService.contexteUtilisateur;

    this.utilisateurService.obtenirUtilisateurPilotage(Number(this.utilisateurId)).subscribe(res => {
      this.user = res;
      this.numeroEmploye = this.user?.numeroEmploye ?? '';
      this.initializeRoles();
      this.majEntiteDispoAdmin();
      this.initializeEntitesVisionnement();
      this.initializeEtiquettes();
      this.isLoading = false;
    });

    this.entitesAccessibles = this.contexteUtilisateur.entitesAccessibles.filter(re => !re.estGroupe).map(re => ({ id: re.entiteId, nom: re.nom }));
    this.estPiloteGroupe = this.contexteUtilisateur.entitesAccessibles.filter(re => re.estGroupe).length > 0;

    this.majEntiteDispoAdmin();
  }

  initializeRoles(): void {
    if (this.user) {
      const piloteRoleId = this.getPiloteRoleId();
      const groupEntities = this.contexteUtilisateur.entitesAccessibles.filter(re => re.estGroupe);

      if (!this.estPilote)
        this.estPilote = this.user.roleEntitesAccessibles.some(re =>
          re.roleId === piloteRoleId && groupEntities.some(ge => ge.entiteId === re.entiteId)
        );

      this.estAdmin = this.user.roleEntitesAccessibles.some(re => re.roleId === this.getAdminRoleId());

      if (this.estAdmin) {
        this.entiteAdmins = this.user.roleEntitesAccessibles
          .filter(re => re.roleId === this.getAdminRoleId())
          .map(re => {
            const entite = this.contexteUtilisateur.entitesAccessibles.find(e => e.entiteId === re.entiteId);
            return { id: re.entiteId, nom: entite ? entite.nom : '' };
          });
      }
    }
  }

  initializeEntitesVisionnement(): void {
    this.entitesVisionnement = this.entitesAccessibles;
    if (this.user) {
      const utilisateurRole = this.user.roleEntitesAccessibles.find(re => re.roleId === this.getUtilisateurRoleId());
      if (utilisateurRole) {
        this.entiteIdVisioSelectionne = utilisateurRole.entiteId;
      }
    }
  }

  getPiloteRoleId(): number {
    return this.contexteUtilisateur.roles.find(r => r.code.toUpperCase() === 'PILOTE')?.roleId || 0;
  }

  getAdminRoleId(): number {
    return this.contexteUtilisateur.roles.find(r => r.code.toUpperCase() === 'ADMIN')?.roleId || 0;
  }

  getUtilisateurRoleId(): number {
    return this.contexteUtilisateur.roles.find(r => r.code.toUpperCase() === 'UTILISATEUR')?.roleId || 0;
  }

  majEntiteDispoAdmin(): void {
    this.entitesDispoAdmin = this.entitesAccessibles.filter(
      entite => !this.entiteAdmins.some(selected => selected.id === entite.id)
    );
    this.entiteIdAdminSelectionnee = null;
  }

  private filterEtiquettesBasedOnAccess(): EtiquetteModel[] {
    const groupEntities = this.contexteUtilisateur.entitesAccessibles.filter(e => e.estGroupe);

    const entityIds = new Set<number>();

    groupEntities.forEach(e => entityIds.add(e.entiteId));

    if (this.entiteIdVisioSelectionne) {
      entityIds.add(this.entiteIdVisioSelectionne);
    }

    var res = this.contexteUtilisateur.toutesLesEtiquettes.filter(etiquette =>
      etiquette.entiteIds.some(entityId => entityIds.has(entityId))
    );

    return res;
  }


  onRoleChange(role: 'pilote' | 'admin'): void {
    if (role === 'pilote') {
      this.estAdmin = false;
    } else {
      this.estPilote = false;
      if (this.entitesAccessibles.length === 1) {
        this.entiteAdmins.push(this.entitesAccessibles[0]);
      }
    }

    if (role === 'admin' && !this.estAdmin) {
      this.entiteAdmins = [];
    }

    this.majEntiteDispoAdmin();
  }

  private refreshEtiquettes(): void {
    const availableEtiquettes = this.filterEtiquettesBasedOnAccess();

    this.etiquettesSelectionnees = this.etiquettesSelectionnees.filter(
      selected => availableEtiquettes.some(available => available.etiquetteId === selected.etiquetteId)
    );

    this.etiquettesDisponibles = availableEtiquettes.filter(
      etiquette => !this.etiquettesSelectionnees.some(
        selected => selected.etiquetteId === etiquette.etiquetteId
      )
    );
  }

  onEntiteVisionnementChange(value: any): void {
    this.entiteIdVisioSelectionne = Number(value);
    this.refreshEtiquettes();
  }

  onEntiteAdminSelected(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const entiteId = Number(selectElement.value);
    const entiteSelectionne = this.entitesDispoAdmin.find(e => e.id === entiteId);
    if (entiteSelectionne) {
      this.entiteAdmins.push(entiteSelectionne);
      this.majEntiteDispoAdmin();
    }
  }

  removeSelectedEntity(id: number): void {
    this.entiteAdmins = this.entiteAdmins.filter(entite => entite.id !== id);
    this.majEntiteDispoAdmin();
  }

  onNumeroEmployeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.numeroEmploye = input.value;
  }

  boutonSauvegardeDesactive(): boolean {
    return (this.estAdmin && this.entitesDispoAdmin.length > 0 && this.entiteAdmins.length === 0) ||
      (this.entitesVisionnement.length > 0 && !this.entiteIdVisioSelectionne);
  }

  initializeEtiquettes(): void {
    const availableEtiquettes = this.filterEtiquettesBasedOnAccess();

    if (this.user && this.user.etiquetteIdsAccessibles) {
      this.etiquettesSelectionnees = availableEtiquettes.filter(
        e => this.user.etiquetteIdsAccessibles!.includes(e.etiquetteId)
      );
      this.etiquettesDisponibles = availableEtiquettes.filter(
        e => !this.user.etiquetteIdsAccessibles!.includes(e.etiquetteId)
      );
    } else {
      this.etiquettesDisponibles = availableEtiquettes;
      this.etiquettesSelectionnees = [];
    }
  }

  onEtiquetteSelected(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const etiquetteId = Number(selectElement.value);
    const etiquetteSelectionnee = this.etiquettesDisponibles.find(e => e.etiquetteId === etiquetteId);
    if (etiquetteSelectionnee) {
      this.etiquettesSelectionnees.push(etiquetteSelectionnee);
      this.etiquettesDisponibles = this.etiquettesDisponibles.filter(e => e.etiquetteId !== etiquetteId);
      this.etiquetteIdSelectionnee = null;
    }
  }

  removeSelectedEtiquette(id: number): void {
    const etiquetteToRemove = this.etiquettesSelectionnees.find(e => e.etiquetteId === id);
    if (etiquetteToRemove) {
      this.etiquettesSelectionnees = this.etiquettesSelectionnees.filter(e => e.etiquetteId !== id);
      this.etiquettesDisponibles.push(etiquetteToRemove);
    }
  }

  saveUser(): void {
    if (this.user) {
      this.estEnCoursDeEnregistrement = true;

      const roleEntites: RoleEntitePilotage[] = [];

      if (this.estPilote) {
        const piloteGroupeId = this.contexteUtilisateur.entitesAccessibles.find(re => re.estGroupe)?.entiteId;
        if (piloteGroupeId) {
          roleEntites.push({ roleId: this.getPiloteRoleId(), entiteId: piloteGroupeId });
        }
      }

      if (this.estAdmin) {
        this.entiteAdmins.forEach(entite => {
          roleEntites.push({ roleId: this.getAdminRoleId(), entiteId: entite.id });
        });
      }

      if (this.entiteIdVisioSelectionne) {
        roleEntites.push(new RoleEntitePilotage(this.getUtilisateurRoleId(), this.entiteIdVisioSelectionne));
      }

      const modifierUtilisateurModel = new ModifierUtilisateurPilotageModel(
        this.user.utilisateurId,
        roleEntites,
        this.numeroEmploye,
        this.contexteUtilisateur.entitesAccessibles.map(e => e.entiteId),
        this.contexteUtilisateur.toutesLesEtiquettes.map(e => e.etiquetteId),
        this.etiquettesSelectionnees.map(e => e.etiquetteId)
      );

      this.utilisateurService.modifierUtilisateur(modifierUtilisateurModel).subscribe({
        next: () => {
          this.estEnCoursDeEnregistrement = false;

          this.router.navigate(['/utilisateurs-managment']);
        },
      });
    }
  }

}
